import React, { useState, useCallback, useRef, useEffect } from 'react';
import './CostAnalysis.css';
import { MarkdownRenderer } from './MarkdownRenderer';
import { apiClient, PLAYGROUND } from '../services/apiClient';

type ImplicitLog = {
    _id?: string;
    question_url: string;
    solution_url: string;
    socket_id?: string;
    question_attempt_id: string;
    hint?: string;
    error?: string;
    correction?: string;
    y?: number;
    bounding_box?: any;
    llm_used?: string;
    contains_diagram?: boolean;
    createdAt?: number;
    updatedAt?: number;
    solution_complete?: boolean;
    solution_lines?: any;
    llm_ocr_lines?: any;
    time_taken?: number;
};

type CheckerResponse = {
    success?: boolean;
    message?: string;
    data?: any;
    error?: string;
};

type ImplicitCheckerReplayProps = {
    environment?: string;
};

const ImplicitCheckerReplay: React.FC<ImplicitCheckerReplayProps> = ({ environment }) => {
    const [attemptId, setAttemptId] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [implicitLogs, setImplicitLogs] = useState<ImplicitLog[]>([]);
    const [selectedLog, setSelectedLog] = useState<ImplicitLog | null>(null);
    const [sending, setSending] = useState<boolean>(false);
    const [checkerResponse, setCheckerResponse] = useState<CheckerResponse | null>(null);
    const prevEnvironmentRef = useRef<string | undefined>(undefined);

    // Clear data when environment changes
    useEffect(() => {
        const prevEnv = prevEnvironmentRef.current;
        if (prevEnv !== undefined && prevEnv !== environment) {
            console.log('[ImplicitCheckerReplay] Environment changed from', prevEnv, 'to', environment);
            setImplicitLogs([]);
            setSelectedLog(null);
            setCheckerResponse(null);
            setAttemptId('');
        }
        prevEnvironmentRef.current = environment;
    }, [environment]);

    const formatTimestamp = (ms?: number) => {
        if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '—';
        try {
            return new Date(ms).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        } catch (_) {
            return new Date(ms).toLocaleString();
        }
    };

    const loadImplicitLogs = useCallback(async () => {
        const trimmedId = attemptId.trim();
        if (!trimmedId) {
            setError('Please enter an attempt ID');
            return;
        }

        setLoading(true);
        setError(null);
        setImplicitLogs([]);
        setSelectedLog(null);
        setCheckerResponse(null);

        try {
            const data = await apiClient.get<any>(`/api/conversations/${trimmedId}`, PLAYGROUND);
            const logs = Array.isArray(data?.implicitAttempts) ? data.implicitAttempts : [];

            if (logs.length === 0) {
                setError('No implicit logs found for this attempt ID');
            } else {
                setImplicitLogs(logs);
                // Auto-select the most recent log
                setSelectedLog(logs[0]);
            }
        } catch (err: any) {
            console.error('Error loading implicit logs:', err);
            setError(err.message || 'Failed to load implicit logs');
        } finally {
            setLoading(false);
        }
    }, [attemptId]);

    const sendToChecker = useCallback(async () => {
        if (!selectedLog) {
            setError('No log selected to send');
            return;
        }

        setSending(true);
        setError(null);
        setCheckerResponse(null);

        try {
            const result = await apiClient.post<any>('/api/implicit-checker/send', PLAYGROUND, {
                attemptId: attemptId.trim(),
                implicitLog: selectedLog,
            });
            setCheckerResponse(result);
        } catch (err: any) {
            console.error('Error sending to checker:', err);
            setError(err.message || 'Failed to send to checker service');
            setCheckerResponse({ success: false, error: err.message });
        } finally {
            setSending(false);
        }
    }, [selectedLog, attemptId]);

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !loading) {
            loadImplicitLogs();
        }
    };

    return (
        <div className="cost-dashboard" style={{ background: 'transparent' }}>
            <div className="dashboard-content">
                {/* Header Section */}
                <div className="table-container">
                    <div className="table-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h3>🔍 Implicit Checker Session Replay</h3>
                            <span style={{
                                padding: '4px 12px',
                                fontSize: '13px',
                                fontWeight: '600',
                                borderRadius: '5px',
                                backgroundColor: environment === 'local' ? '#fff3cd' : environment === 'staging' ? '#d1ecf1' : '#d4edda',
                                color: '#333',
                                border: '1px solid #ccc'
                            }}>
                                {environment === 'local' ? '🟡 Local' : environment === 'staging' ? '🔵 Staging' : '🟢 Production'}
                            </span>
                        </div>
                    </div>

                    {/* Input Section */}
                    <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '8px', marginBottom: '20px' }}>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                Attempt ID
                            </label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder="Enter attempt ID..."
                                    value={attemptId}
                                    onChange={(e) => setAttemptId(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={loading}
                                    style={{ flex: 1 }}
                                />
                                <button
                                    className="filter-btn"
                                    onClick={loadImplicitLogs}
                                    disabled={loading || !attemptId.trim()}
                                >
                                    {loading ? 'Loading...' : 'Load Implicit Logs'}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div style={{
                                padding: '10px',
                                background: '#fee',
                                border: '1px solid #fcc',
                                borderRadius: '4px',
                                color: '#c00',
                                marginTop: '10px'
                            }}>
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Implicit Logs List */}
                    {implicitLogs.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ marginBottom: '12px' }}>Implicit Logs ({implicitLogs.length})</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {implicitLogs.map((log, index) => (
                                    <div
                                        key={log._id || index}
                                        onClick={() => setSelectedLog(log)}
                                        style={{
                                            padding: '15px',
                                            border: selectedLog === log ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            background: selectedLog === log ? '#f0f9ff' : '#fff',
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <span style={{ fontWeight: 600, fontSize: '14px' }}>
                                                Log #{implicitLogs.length - index}
                                            </span>
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                {formatTimestamp(log.createdAt)}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#374151' }}>
                                            {log.hint && <div><strong>Hint:</strong> {log.hint.substring(0, 100)}...</div>}
                                            {log.error && <div><strong>Error:</strong> {log.error.substring(0, 100)}...</div>}
                                            {log.correction && <div><strong>Correction:</strong> {log.correction.substring(0, 100)}...</div>}
                                        </div>
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                                            <span>LLM: {log.llm_used || 'N/A'}</span>
                                            {log.y !== undefined && <span style={{ marginLeft: '12px' }}>Y: {log.y}</span>}
                                            {log.contains_diagram !== undefined && <span style={{ marginLeft: '12px' }}>Diagram: {log.contains_diagram ? 'Yes' : 'No'}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Selected Log Details */}
                    {selectedLog && (
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h4>Selected Log Details</h4>
                                <button
                                    className="primary-btn"
                                    onClick={sendToChecker}
                                    disabled={sending || !selectedLog}
                                    style={{
                                        padding: '8px 16px',
                                        background: sending ? '#94a3b8' : '#4f46e5',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: sending ? 'not-allowed' : 'pointer',
                                        fontWeight: 600,
                                    }}
                                >
                                    {sending ? 'Sending...' : '📤 Send to Checker'}
                                </button>
                            </div>

                            {/* Images Display */}
                            <div style={{ marginBottom: '20px' }}>
                                <h5 style={{ marginBottom: '10px' }}>📷 Reference Images</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div>
                                        <p style={{ fontWeight: 600, marginBottom: '8px' }}>Question</p>
                                        {selectedLog.question_url ? (
                                            <img
                                                src={selectedLog.question_url}
                                                alt="Question"
                                                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                            />
                                        ) : (
                                            <div style={{ padding: '40px', background: '#f3f4f6', borderRadius: '8px', textAlign: 'center', color: '#6b7280' }}>
                                                No question image
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <p style={{ fontWeight: 600, marginBottom: '8px' }}>Solution</p>
                                        {selectedLog.solution_url ? (
                                            <img
                                                src={selectedLog.solution_url}
                                                alt="Solution"
                                                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                            />
                                        ) : (
                                            <div style={{ padding: '40px', background: '#f3f4f6', borderRadius: '8px', textAlign: 'center', color: '#6b7280' }}>
                                                No solution image
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Log Content */}
                            <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                                <h5 style={{ marginBottom: '10px' }}>📝 Log Content</h5>
                                {selectedLog.hint && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong style={{ color: '#059669' }}>Hint:</strong>
                                        <div style={{ marginTop: '4px', padding: '10px', background: '#fff', borderRadius: '4px' }}>
                                            <MarkdownRenderer content={selectedLog.hint} />
                                        </div>
                                    </div>
                                )}
                                {selectedLog.error && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong style={{ color: '#dc2626' }}>Error:</strong>
                                        <div style={{ marginTop: '4px', padding: '10px', background: '#fff', borderRadius: '4px' }}>
                                            <MarkdownRenderer content={selectedLog.error} />
                                        </div>
                                    </div>
                                )}
                                {selectedLog.correction && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong style={{ color: '#2563eb' }}>Correction:</strong>
                                        <div style={{ marginTop: '4px', padding: '10px', background: '#fff', borderRadius: '4px' }}>
                                            <MarkdownRenderer content={selectedLog.correction} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Metadata */}
                            <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px' }}>
                                <h5 style={{ marginBottom: '10px' }}>🔧 Metadata</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '13px' }}>
                                    <div><strong>Socket ID:</strong> {selectedLog.socket_id || 'N/A'}</div>
                                    <div><strong>LLM Used:</strong> {selectedLog.llm_used || 'N/A'}</div>
                                    <div><strong>Y Coordinate:</strong> {selectedLog.y !== undefined ? selectedLog.y : 'N/A'}</div>
                                    <div><strong>Contains Diagram:</strong> {selectedLog.contains_diagram ? 'Yes' : 'No'}</div>
                                    <div><strong>Solution Complete:</strong> {selectedLog.solution_complete ? 'Yes' : 'No'}</div>
                                    <div><strong>Time Taken:</strong> {selectedLog.time_taken ? `${selectedLog.time_taken}ms` : 'N/A'}</div>
                                    <div><strong>Created:</strong> {formatTimestamp(selectedLog.createdAt)}</div>
                                    <div><strong>Updated:</strong> {formatTimestamp(selectedLog.updatedAt)}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Checker Response */}
                    {checkerResponse && (
                        <div style={{ marginTop: '20px' }}>
                            <h4 style={{ marginBottom: '12px' }}>✅ Checker Service Response</h4>
                            <div style={{
                                padding: '15px',
                                background: checkerResponse.success === false ? '#fee' : '#f0fdf4',
                                border: `1px solid ${checkerResponse.success === false ? '#fcc' : '#bbf7d0'}`,
                                borderRadius: '8px',
                            }}>
                                <pre style={{
                                    margin: 0,
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}>
                                    {JSON.stringify(checkerResponse, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImplicitCheckerReplay;